import { FC, useState, useEffect } from 'react';
import { SpaceBetween, Textarea, Button, Box, ColumnLayout } from '@amzn/awsui-components-react';
import yaml, { JSON_SCHEMA } from 'js-yaml';
import { UatToolCall, UatToolFeedback, JsonValue } from 'src/store/interfaces/Uat.types';
import ToolCallCard from './ToolCallCard';

const NO_TOOL_INDEX = -1;
const NO_TOOL_NAME = 'No Tool';

interface QueryFeedbackEditorProps {
  tools: UatToolCall[];
  rawResponse?: string | null;
  initialFeedbacks?: UatToolFeedback[] | null;
  initialRawResponseVote?: 'up' | 'down' | null;
  initialComments?: string;
  onSubmit: (feedbacks: UatToolFeedback[], comments: string, rawResponseVote: 'up' | 'down' | null) => Promise<void>;
  onCancel?: () => void;
  isSubmitting: boolean;
}

const buildDefaultFeedbacks = (tools: UatToolCall[]): UatToolFeedback[] => {
  if (tools.length === 0) {
    return [{ toolIndex: NO_TOOL_INDEX, toolName: NO_TOOL_NAME, vote: null, correctedParameters: null }];
  }
  return tools.map((t, i) => ({ toolIndex: i, toolName: t.toolName, vote: null, correctedParameters: null }));
};

const NO_TOOL_CALL: UatToolCall = {
  toolName: NO_TOOL_NAME,
  toolParameters: {},
  status: 'N/A',
  responseData: null,
  durationMs: null,
};

const QueryFeedbackEditor: FC<QueryFeedbackEditorProps> = ({
  tools,
  rawResponse,
  initialFeedbacks,
  initialRawResponseVote,
  initialComments,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const [feedbacks, setFeedbacks] = useState<UatToolFeedback[]>([]);
  const [rawResponseVote, setRawResponseVote] = useState<'up' | 'down' | null>(null);
  const [comments, setComments] = useState('');
  const [yamlTexts, setYamlTexts] = useState<Record<number, string>>({});
  const [yamlErrors, setYamlErrors] = useState<Record<number, string | null>>({});

  useEffect(() => {
    setFeedbacks(initialFeedbacks?.length ? [...initialFeedbacks] : buildDefaultFeedbacks(tools));
    setRawResponseVote(initialRawResponseVote ?? null);
    setComments(initialComments ?? '');
  }, [tools, initialFeedbacks, initialRawResponseVote, initialComments]);

  const handleFeedbackChange = (index: number, updated: UatToolFeedback) => {
    setFeedbacks((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  };

  const handleRawVote = (vote: 'up' | 'down') => {
    setRawResponseVote((prev) => (prev === vote ? null : vote));
  };

  const handleYamlTextChange = (index: number, text: string) => {
    setYamlTexts((prev) => ({ ...prev, [index]: text }));
    setYamlErrors((prev) => ({ ...prev, [index]: null }));
  };

  const handleSubmit = () => {
    const errors: Record<number, string | null> = {};
    const actualTexts = displayTools.map((t) =>
      yaml.dump(t.toolParameters, { flowLevel: 1, lineWidth: -1, noRefs: true, schema: JSON_SCHEMA }),
    );
    const resolved = feedbacks.map((fb, i) => {
      const text = yamlTexts[i];
      if (text === undefined || text === actualTexts[i]) return fb;
      try {
        const parsed = yaml.load(text, { schema: JSON_SCHEMA }) as Record<string, JsonValue>;
        return { ...fb, correctedParameters: parsed ?? null };
      } catch {
        errors[i] = 'Invalid YAML — please fix before submitting';
        return fb;
      }
    });
    if (Object.values(errors).some(Boolean)) {
      setYamlErrors(errors);
      return;
    }
    onSubmit(resolved, comments, rawResponseVote);
  };

  const displayTools = tools.length > 0 ? tools : [NO_TOOL_CALL];
  const allVoted = feedbacks.length > 0 && feedbacks.every((f) => f.vote !== null);

  return (
    <SpaceBetween size="s">
      {/* Raw Response Vote */}
      {rawResponse && (
        <Box padding={{ left: 'l' }}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box fontSize="heading-s" fontWeight="bold">Raw Response (AskRino)</Box>
            </div>
            <div>
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant={rawResponseVote === 'up' ? 'primary' : 'normal'}
                  iconName="thumbs-up"
                  onClick={() => handleRawVote('up')}
                >
                  Correct
                </Button>
                <Button
                  variant={rawResponseVote === 'down' ? 'primary' : 'normal'}
                  iconName="thumbs-down"
                  onClick={() => handleRawVote('down')}
                >
                  Incorrect
                </Button>
              </SpaceBetween>
            </div>
          </ColumnLayout>
        </Box>
      )}

      {displayTools.map((tool, i) => (
        <ToolCallCard
          key={i}
          toolCall={tool}
          feedback={feedbacks[i] ?? buildDefaultFeedbacks(tools)[i]}
          onFeedbackChange={(fb) => handleFeedbackChange(i, fb)}
          onYamlTextChange={(text) => handleYamlTextChange(i, text)}
          yamlError={yamlErrors[i]}
          editing={true}
        />
      ))}
      <Textarea
        value={comments}
        onChange={({ detail }) => setComments(detail.value)}
        placeholder="Overall comments..."
        rows={2}
      />
      {!allVoted && (
        <Box color="text-status-info" fontSize="body-s">
          Vote on all tool calls before submitting.
        </Box>
      )}
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          {onCancel && (
            <Button variant="link" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting} disabled={!allVoted}>
            Submit Feedback
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
};

export default QueryFeedbackEditor;
